import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { ConfigService } from '@rxap/config';
import { isHeliaEnabled } from '../is-helia-enabled';
import { IpfsHealthService } from '../ipfs/ipfs-health.service';
import { iirdsStore } from '../stores/iirds.store';
import { logGuardResult } from './guard-logger';

/**
 * When the readiness gate applies:
 * - `always` — the route always needs IPFS (e.g. `/upload/cid`).
 * - `cid-primary` — only when the highest-priority available iiRDS source is a
 *   CID, so HTTP-first (`baseUrl`/`url`) loads are never delayed.
 */
export type IpfsReadyGate = 'always' | 'cid-primary';

/**
 * Guards a route that resolves content over IPFS: it lets navigation proceed
 * only once Helia is confirmed reachable, otherwise it redirects to the
 * `/ipfs-connecting` screen which boots the node, shows the live connection
 * status and forwards back here once online (or offers a recovery action on
 * failure).
 *
 * The guard only *reads* {@link IpfsHealthService.ready} (a signal) and never
 * awaits the boot — the connecting screen owns the async `check()` so the user
 * always sees feedback during the up-to-~40s startup instead of a frozen
 * navigation.
 */
function createIpfsReadyGuard(gate: IpfsReadyGate, name: string): CanActivateFn {
  const guard: CanActivateFn = (route, state) => {
    const ipfsHealth = inject(IpfsHealthService);
    const iirds = inject(iirdsStore);
    const router = inject(Router);
    const config = inject(ConfigService);

    if (!isHeliaEnabled(config)) {
      if (gate === 'always') {
        // /upload/cid: IPFS disabled — the whole screen is unusable, send
        // the user back to the source picker instead of the connecting
        // screen (which would try to boot Helia).
        const target = router.createUrlTree(['/upload']);
        return logGuardResult(name, new RedirectCommand(target), {
          reason: 'IPFS disabled via ipfs.enabled=false',
        });
      }
      // cid-primary (/iirds): a cid source is now resolved via a plain HTTP
      // gateway fetch, not Helia, so there is nothing to wait for.
      return logGuardResult(name, true, {
        reason:
          'IPFS disabled — cid source resolved via HTTP gateway, no Helia readiness needed',
      });
    }

    // A loaded container needs no fresh boot; any per-document IPFS fetch
    // reuses the (memoized-online) node.
    if (iirds.hasContainer()) {
      return logGuardResult(name, true, { reason: 'container already loaded' });
    }

    if (gate === 'cid-primary' && iirds.activeSource() !== 'cid') {
      return logGuardResult(name, true, {
        reason: 'primary source is not cid — IPFS not required',
        activeSource: iirds.activeSource(),
      });
    }

    if (ipfsHealth.ready()) {
      return logGuardResult(name, true, { reason: 'IPFS already online' });
    }

    const target = router.createUrlTree(['/ipfs-connecting'], {
      queryParams: { return: state.url },
    });
    return logGuardResult(name, new RedirectCommand(target), {
      reason: 'IPFS not ready — showing connecting screen',
      return: state.url,
    });
  };
  // Give the returned function a stable name so sequenceGuard's log lines and
  // the guard-logger group by the real guard, not "anonymous".
  Object.defineProperty(guard, 'name', { value: name });
  return guard;
}

/**
 * Gate for the archive display (`/iirds`): waits for Helia only when a CID is
 * the primary source. Composed after `configGuard` (which resolves the source)
 * and before `iirdsGuard` (which performs the CID fetch).
 */
export const ipfsArchiveReadyGuard = createIpfsReadyGuard(
  'cid-primary',
  'ipfsArchiveReadyGuard'
);

/**
 * Gate for the "Load from IPFS CID" upload screen (`/upload/cid`): always waits
 * for Helia, since the whole screen is unusable until IPFS can resolve content.
 */
export const ipfsSourceReadyGuard = createIpfsReadyGuard(
  'always',
  'ipfsSourceReadyGuard'
);
